import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import authUtils from '../utils/authUtils';
import { getApiUrl } from '../config/api';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

export default function AuthCallback() {
  const navigate = useNavigate();
  
  // Get API URL at runtime
  const BACKEND_URL = getApiUrl();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('Connecting to the Field...');
  const [error, setError] = useState(null);
  
  useEffect(() => {
    // Use useRef for processed flag - synchronously check at start
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    
    const processAuth = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = window.location.hash;
        console.log('[Auth] Processing hash:', hash);
        
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        
        if (!sessionIdMatch) {
          console.log('[Auth] No session_id found in hash');
          setStatus('No session found. Returning to landing...');
          setError('No session ID in URL');
          setTimeout(() => navigate('/'), 2000);
          return;
        }
        
        const sessionId = sessionIdMatch[1];
        console.log('[Auth] Found session_id:', sessionId.substring(0, 20) + '...');
        setStatus('Attuning to your presence...');
        
        // Exchange session_id for user data
        console.log('[Auth] Calling backend:', `${BACKEND_URL}/api/auth/session`);
        const response = await fetch(`${BACKEND_URL}/api/auth/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ session_id: sessionId })
        });
        
        console.log('[Auth] Response status:', response.status);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('[Auth] Backend error:', errorText);
          throw new Error(`Authentication failed: ${response.status} - ${errorText}`);
        }
        
        const userData = await response.json();
        console.log('[Auth] User authenticated:', userData.email);
        console.log('[Auth] Onboarding status:', userData.has_completed_onboarding);
        
        // Store session token for cross-origin requests (custom domains like alvins.place)
        if (userData.session_token) {
          console.log('[Auth] Storing session token for cross-origin support');
          authUtils.setToken(userData.session_token);
        }
        
        // Check if this is Betsy (primary user) - she should skip onboarding
        const isBetsy = userData.email?.toLowerCase().includes('betsy') || 
                        userData.email?.toLowerCase().includes('mythologyofhumanity.com');
        
        if (isBetsy && !userData.has_completed_onboarding) {
          // Auto-complete onboarding for Betsy
          console.log('[Auth] Primary user detected - completing onboarding automatically');
          try {
            await authUtils.authFetch(`${BACKEND_URL}/api/auth/complete-onboarding`, {
              method: 'POST'
            });
            userData.has_completed_onboarding = true;
          } catch (e) {
            console.error('[Auth] Failed to complete onboarding:', e);
          }
        }
        
        // Check for invitation code in session storage
        const invitationCode = sessionStorage.getItem('invitation_code');
        if (invitationCode) {
          setStatus('Applying invitation key...');
          try {
            await fetch(`${BACKEND_URL}/api/auth/use-invitation`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ code: invitationCode })
            });
          } catch (e) {
            console.log('[Auth] Invitation code not applied:', e);
          }
          sessionStorage.removeItem('invitation_code');
        }
        
        setStatus('Welcome to the Field...');
        
        // Route based on onboarding status
        setTimeout(() => {
          if (!userData.has_completed_onboarding) {
            // New users go to onboarding
            console.log('[Auth] Redirecting to onboarding');
            navigate('/onboarding', { 
              state: { user: userData },
              replace: true 
            });
          } else if (isBetsy) {
            // Betsy goes to Sanctuary (her home)
            console.log('[Auth] Redirecting primary user to Sanctuary');
            navigate('/sanctuary', { 
              state: { user: userData },
              replace: true 
            });
          } else {
            // Other returning users go to vessels page
            console.log('[Auth] Redirecting to vessels');
            navigate('/vessels', { 
              state: { user: userData },
              replace: true 
            });
          }
        }, 1000);
        
      } catch (err) {
        console.error('[Auth] Error:', err);
        setError(err.message);
        setStatus('Connection interrupted. Returning...');
        setTimeout(() => navigate('/'), 3000);
      }
    };
    
    processAuth();
  }, [navigate]);
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-cosmos-bg to-cosmos-deep flex items-center justify-center">
      <motion.div
        className="text-center max-w-md px-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        {/* Breathing Orb */}
        <motion.div
          className={`w-24 h-24 mx-auto mb-8 rounded-full ${error ? 'bg-rose-gold/30' : 'bg-etheric/30'}`}
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.5, 0.8, 0.5]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          style={{
            boxShadow: error ? '0 0 60px rgba(183, 110, 121, 0.4)' : '0 0 60px rgba(74, 111, 165, 0.4)'
          }}
        />
        
        <h2 className="font-heading text-2xl text-starlight mb-2">
          {status}
        </h2>
        
        {error && (
          <p className="text-rose-gold/80 text-sm mt-4 p-3 glass rounded-xl">
            {error}
          </p>
        )}
        
        <div className="flex justify-center gap-2 mt-4">
          {['◈', '✦', '◯'].map((glyph, i) => (
            <motion.span
              key={i}
              className={`text-xl ${error ? 'text-rose-gold' : 'text-etheric'}`}
              animate={{ opacity: [0.3, 1, 0.3] }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                delay: i * 0.2
              }}
            >
              {glyph}
            </motion.span>
          ))}
        </div>
      </motion.div>
    </div>
  );
}
